/**
 * environment.ts — the World (paintable ground truth) and the Env (one run).
 *
 * Two layers:
 *
 *   - `World` — the static grid of true cell classes the USER paints. It is
 *     the ground truth. The agent never sees it directly; it sees noisy
 *     sensor readings, which the Belief turns into a belief map. Painting
 *     mid-run is exactly the "non-stationary world" case the online RL loop
 *     is for.
 *
 *   - `Env` — one run's transient state: the agent's pose, the visited set,
 *     the belief map, the step counter, the reward function, the done flag.
 *     `reset()` starts a run from a pose; `step(a)` advances one tick and
 *     returns (reward, done, event).
 *
 * Reward design (v5 prototype):
 *
 *   +1.0          entering a gold cell (gold consumed, removed from the World)
 *   -0.3          flat bump cost when a leap is blocked
 *   -0.05/extra   additional cost per intended-but-unwalked cell of a blocked
 *                 leap (a length-8 leap that bumps at cell 1 costs -0.3 -0.05·7)
 *   +0.15         entering a NEW free cell (coverage shaping — the dense reward
 *                 that makes online collection informative)
 *   -0.01         per cell walked (liveness penalty)
 *
 * The bump cost scales with intended length so committing to long leaps into
 * stone-heavy areas is properly painful — this was the fix that stopped the
 * agent from overfitting to length-8 perimeter walks.
 *
 * The leap action space:
 *   8 directions × 4 leap lengths {1, 2, 4, 8} = 32 actions.
 *   Action a → (dir = a % 8, length = LEAP_LENGTHS[a / 8]).
 */

import {
  Belief,
  FREE,
  GOLD,
  STONE,
  sampleReading,
  SENSOR_RADIUS,
} from './belief';

/** The 8 single-cell directions, indexed 0..7. (dr, dc) — row grows South,
 *  col grows East.
 *
 *  Cardinals first (E/W/S/N), then diagonals (NE/NW/SE/SW). Diagonals are
 *  essential for vein-following: a spiral arm is a curve, and following a
 *  curve with only cardinal moves requires zig-zagging; with diagonals the
 *  curve is smooth. */
export const DIRECTIONS: ReadonlyArray<readonly [number, number]> = [
  [0, 1], // 0 = E
  [0, -1], // 1 = W
  [1, 0], // 2 = S
  [-1, 0], // 3 = N
  [-1, 1], // 4 = NE
  [-1, -1], // 5 = NW
  [1, 1], // 6 = SE
  [1, -1], // 7 = SW
];
export const N_DIRS = 8;

/** Leap lengths the Q-net chooses between. The action space is the Cartesian
 *  product {8 directions} × {these lengths}, so N_ACTIONS = 8 × 4 = 32.
 *  A leap of length L in direction D walks L cells in direction D in ONE
 *  tick (sensing + rewarding each).
 *
 *  Lengths grow by powers of 2 — this gives the agent both fine control near
 *  gold (length 1) and the ability to commit to long traversals into
 *  unexplored area (length 8) without spending a tick per cell. */
export const LEAP_LENGTHS: ReadonlyArray<number> = [1, 2, 4, 8];

/** Total action count = directions × leap lengths. */
export const N_ACTIONS = N_DIRS * LEAP_LENGTHS.length; // 32

/** Decode an action id into (direction index, leap length). */
export function decodeAction(a: number): { dir: number; len: number } {
  const dir = a % N_DIRS;
  const len = LEAP_LENGTHS[(a / N_DIRS) | 0];
  return { dir, len };
}

/** Encode (direction index, leap-length-index) back into an action id. */
export function encodeAction(dir: number, lenIdx: number): number {
  return lenIdx * N_DIRS + dir;
}

/** Backwards-compat alias — legacy code (canMove, renderer) reads ACTIONS. */
export const ACTIONS = DIRECTIONS;

/** True if direction `d` is a diagonal move (both row and col change). */
export function isDiagonal(d: number): boolean {
  const [dr, dc] = DIRECTIONS[d];
  return dr !== 0 && dc !== 0;
}

/**
 * Is the move from (r, c) by direction `dir` legal on the given truth grid?
 * Blocks out-of-bounds, stones at the destination, AND corner-cutting on
 * diagonals: a diagonal move is blocked if EITHER of the two orthogonal
 * cells it "cuts between" is a stone.
 *
 * `stoneAt(r, c)` is a callback so this works for both the World's true
 * stones (env.step) and the belief's believed stones (feature computation).
 */
export function canMove(
  r: number,
  c: number,
  dir: number,
  inBounds: (r: number, c: number) => boolean,
  stoneAt: (r: number, c: number) => boolean,
): boolean {
  const [dr, dc] = DIRECTIONS[dir];
  const nr = r + dr;
  const nc = c + dc;
  if (!inBounds(nr, nc)) return false;
  if (stoneAt(nr, nc)) return false;
  if (dr !== 0 && dc !== 0) {
    // Diagonal: block if EITHER orthogonal neighbour is stone.
    if (stoneAt(r + dr, c) || stoneAt(r, c + dc)) return false;
  }
  return true;
}

export interface RewardWeights {
  gold: number;           // +1.0
  bumpBase: number;       // -0.3
  bumpPerUnwalked: number;// -0.05
  coverage: number;       // +0.15
  step: number;           // -0.01
}

export const DEFAULT_REWARDS: RewardWeights = {
  gold: 1.0,
  bumpBase: -0.3,
  bumpPerUnwalked: -0.05,
  coverage: 0.15,
  step: -0.01,
};

/**
 * The paintable ground-truth grid. Cell values are FREE / GOLD / STONE.
 * The UI paints on this; the Env senses against it; rendering reads it.
 *
 * `goldCount` is maintained incrementally so the done-check (`goldLeft <= 0`)
 * is O(1) even when the user paints thousands of cells mid-run.
 */
export class World {
  readonly grid: number;
  readonly cells: Uint8Array;
  private goldCount = 0;

  constructor(grid: number) {
    this.grid = grid;
    this.cells = new Uint8Array(grid * grid); // all FREE
  }

  /** Paint a cell. Respects mutual exclusion: painting GOLD or STONE
   *  overwrites whatever was there; painting FREE erases. Keeps goldCount
   *  consistent so the Env's done check stays correct under live edits. */
  paint(r: number, c: number, kind: number): void {
    if (!this.inBounds(r, c)) return;
    const i = r * this.grid + c;
    const prev = this.cells[i];
    if (prev === GOLD && kind !== GOLD) this.goldCount--;
    if (kind === GOLD && prev !== GOLD) this.goldCount++;
    this.cells[i] = kind;
  }

  /** Bulk-set from a generator (spiral, random, clear). Resets goldCount. */
  loadFrom(cells: ArrayLike<number>): void {
    const n = this.cells.length;
    for (let i = 0; i < n; i++) this.cells[i] = cells[i] ?? 0;
    this.goldCount = 0;
    for (let i = 0; i < n; i++) {
      if (this.cells[i] === GOLD) this.goldCount++;
    }
  }

  inBounds(r: number, c: number): boolean {
    return r >= 0 && r < this.grid && c >= 0 && c < this.grid;
  }

  get totalGold(): number {
    return this.goldCount;
  }

  get kindArray(): Uint8Array {
    return this.cells;
  }
}

export interface StepOutcome {
  reward: number;
  done: boolean;
  /** Human-readable event for the HUD: 'gold' | 'bump' | 'new' | 'step'. */
  event: 'gold' | 'bump' | 'new' | 'step';
  /** The new pose after the step. */
  r: number;
  c: number;
}

/**
 * One episode's run state. Cheap to construct; `reset()` is what starts a run.
 * The agent's pose, the visited set, and the belief map all live here.
 */
export class Env {
  readonly belief: Belief;
  r = 0;
  c = 0;
  steps = 0;
  goldLeft = 0;
  done = false;
  /** Sensor reliability P(reading == truth). Mutable so the UI can tune it
   *  live; applied on the next sensing pass. */
  sensorP: number;
  /** Per-episode step cap (see constructor doc). Mutable for tuning. */
  maxSteps: number;
  /** Reward weights. Mutable so the UI can tune them live — the agent's
   *  tendency to grab gold vs explore vs avoid bumps is sensitive to these,
   *  and the demo exposes them as sliders for live debugging. The object is
   *  shared by reference, so mutate its fields, don't replace it. */
  rewards: RewardWeights;
  /** Visited set as a bit matrix — fast O(1) check for the coverage bonus. */
  private visited: Uint8Array;

  constructor(
    readonly world: World,
    readonly rng: () => number,
    sensorP: number = 0.85,
    readonly beliefLr: number = 0.5,
    rewards: RewardWeights = DEFAULT_REWARDS,
    /** Per-episode step cap. When the agent hits this without clearing the
     *  gold the episode ends (env.done = true) and the orchestrator starts
     *  the next one. */
    maxSteps: number = 400,
    readonly priorBar: number = 0.1,
  ) {
    this.sensorP = sensorP;
    this.maxSteps = maxSteps;
    this.rewards = rewards;
    this.belief = new Belief(world.grid);
    this.visited = new Uint8Array(world.grid * world.grid);
  }

  /**
   * Start a run from pose (r, c). Builds the prior belief map (gold fraction
   * as the cold prior; a small stone prior), clears the start cell (the
   * agent always knows where it is), senses the neighbourhood so the first
   * observation is informative.
   */
  reset(r: number, c: number): void {
    const g = this.world.grid;
    this.r = r;
    this.c = c;
    this.steps = 0;
    this.goldLeft = this.world.totalGold;
    this.done = false;
    this.visited.fill(0);
    const total = g * g;
    const priorGold = total > 0 ? this.goldLeft / total : 0;
    this.belief.reset(priorGold, this.priorBar, r, c);
    this.visited[r * g + c] = 1;
    this.senseNeighbourhood();
  }

  /** Sense all cells in the (2·SENSOR_RADIUS + 1)² neighbourhood around the
   *  current pose. SENSOR_RADIUS = 3 → the agent sees a 7×7 window. */
  private senseNeighbourhood(): void {
    const g = this.world.grid;
    for (let dr = -SENSOR_RADIUS; dr <= SENSOR_RADIUS; dr++) {
      for (let dc = -SENSOR_RADIUS; dc <= SENSOR_RADIUS; dc++) {
        const rr = this.r + dr;
        const cc = this.c + dc;
        if (!this.world.inBounds(rr, cc)) continue;
        if (rr === this.r && cc === this.c) continue;
        const truth = this.world.cells[rr * g + cc];
        const reading = sampleReading(truth, this.sensorP, this.rng);
        this.belief.applyReading(rr, cc, reading, this.beliefLr);
      }
    }
  }

  /**
   * Execute one leap action. Decodes `action` into (direction, length) and
   * walks up to `length` cells in that direction in ONE tick, sensing +
   * rewarding each traversed cell. Stops early on a stone/wall/corner-cut.
   *
   * **Bump-loop fix (v5):** every action consumes AT LEAST one step, even if
   * it bumps immediately. Without this, a cornered agent could rack up bumps
   * without advancing `steps`, and the episode cap would never trigger — the
   * episode would hang. The Python prototype had this bug; the TS port keeps
   * the fix.
   *
   * Reward accounting is HONEST: each traversed cell earns its own step
   * penalty + coverage/gold bonus. A length-8 leap into unexplored space
   * earns ~8 coverage bonuses (cheap); a length-8 leap through explored
   * space pays ~8 step penalties (expensive). When a leap bumps after k
   * cells, the (length − k) unwalked cells each cost `bumpPerUnwalked`.
   *
   * Decay + sensing happen ONCE per leap (at the final pose).
   */
  step(action: number): StepOutcome {
    if (this.done) {
      return { reward: 0, done: true, event: 'step', r: this.r, c: this.c };
    }
    const { dir, len } = decodeAction(action);
    const rw = this.rewards;
    const g = this.world.grid;
    const inBounds = (rr: number, cc: number) => this.world.inBounds(rr, cc);
    const stoneAt = (rr: number, cc: number) => this.world.cells[rr * g + cc] === STONE;

    // Bump-loop fix: every action consumes ≥1 step regardless of outcome.
    this.steps++;

    let totalReward = 0;
    let event: StepOutcome['event'] = 'step';
    let walked = 0;
    let bumped = false;

    for (let i = 0; i < len; i++) {
      // One-cell legality check for direction `dir`.
      if (!canMove(this.r, this.c, dir, inBounds, stoneAt)) {
        totalReward += rw.bumpBase;
        totalReward += rw.bumpPerUnwalked * (len - walked);
        if (walked === 0) event = 'bump';
        bumped = true;
        break;
      }
      const [dr, dc] = DIRECTIONS[dir];
      const nr = this.r + dr;
      const nc = this.c + dc;
      const idx = nr * g + nc;
      totalReward += rw.step;
      walked++;
      if (this.world.cells[idx] === GOLD) {
        totalReward += rw.gold;
        event = 'gold';
        this.world.paint(nr, nc, FREE);
        this.goldLeft--;
      } else if (!this.visited[idx]) {
        totalReward += rw.coverage;
        if (event !== 'gold') event = 'new';
      } else {
        if (event !== 'gold' && event !== 'new') event = 'step';
      }
      this.r = nr;
      this.c = nc;
      this.visited[idx] = 1;
      this.belief.clearCell(nr, nc);
      if (this.goldLeft <= 0 || this.steps >= this.maxSteps) {
        this.done = true;
        break;
      }
    }
    void bumped;

    if (this.goldLeft <= 0 || this.steps >= this.maxSteps) this.done = true;

    // Decay the recency channel ONCE per leap, then re-sense at the final
    // pose and re-mark the current cell (the decay above set it to 0.97).
    this.belief.decayRecency();
    this.belief.bRecent[this.r * g + this.c] = 1;
    this.senseNeighbourhood();
    return { reward: totalReward, done: this.done, event, r: this.r, c: this.c };
  }

  /** Visited matrix for the renderer (the "explored area" overlay). */
  get visitedCells(): Uint8Array {
    return this.visited;
  }

  /**
   * Re-sync `goldLeft` from the World's current gold count. Call this after
   * the user paints gold mid-run so the done-check (`goldLeft <= 0`) and the
   * HUD's "gold left" counter stay honest.
   */
  resyncGoldLeft(): void {
    this.goldLeft = this.world.totalGold;
    if (this.goldLeft <= 0 && this.steps > 0) {
      this.done = true;
    } else if (this.goldLeft > 0) {
      this.done = false;
    }
  }
}
